import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  VStack,
  HStack,
  Box,
  Text,
  Input,
  Switch,
  useToast,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Spinner,
} from '@chakra-ui/react';
import { useAuth } from '../../contexts/AuthContext';
import userService from '../../services/userService';

interface PersonalLimitsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PersonalLimitsModal: React.FC<PersonalLimitsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { userProfile, currentUser, refreshProfile } = useAuth();
  const toast = useToast();
  
  const [loading, setLoading] = useState(false);
  const [limits, setLimits] = useState({
    enabled: false,
    dailyDepositLimit: 100,
    weeklyDepositLimit: 500,
    monthlyDepositLimit: 2000,
    sessionTimeLimit: 120, // minutes
    lossLimit: 50,
  });

  // Initialize with current limits
  useEffect(() => {
    if (userProfile?.personalLimits && isOpen) {
      setLimits({
        enabled: userProfile.personalLimits.enabled || false,
        dailyDepositLimit: userProfile.personalLimits.dailyDepositLimit || 100,
        weeklyDepositLimit: userProfile.personalLimits.weeklyDepositLimit || 500,
        monthlyDepositLimit: userProfile.personalLimits.monthlyDepositLimit || 2000,
        sessionTimeLimit: userProfile.personalLimits.sessionTimeLimit || 120,
        lossLimit: userProfile.personalLimits.lossLimit || 50,
      });
    }
  }, [userProfile, isOpen]);

  const handleToggle = (enabled: boolean) => {
    setLimits(prev => ({ ...prev, enabled }));
  };

  const handleLimitChange = (field: string, value: number) => {
    setLimits(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!currentUser) return;

    setLoading(true);

    try {
      await userService.updatePersonalLimits(currentUser.uid, limits);

      toast({
        title: 'Limits updated',
        description: limits.enabled 
          ? 'Your personal limits are now active' 
          : 'Personal limits have been disabled',
        status: 'success',
        duration: 2000,
      });

      await refreshProfile();
      onClose();
    } catch (error: any) {
      console.error('Error saving limits:', error);
      toast({
        title: 'Error',
        description: error.message || 'Could not save limits',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const inputStyles = {
    bg: '#1C1C1E',
    border: 'none',
    borderRadius: 'xl',
    color: 'white',
    py: 6,
    px: 4,
    fontSize: 'md',
    _placeholder: { color: 'gray.500' },
    _focus: { 
      boxShadow: '0 0 0 1px rgba(255, 107, 53, 0.5)',
      bg: '#252528',
    },
    _hover: { bg: '#252528' },
  };

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins} minutes`;
    if (mins === 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
    return `${hours}h ${mins}m`;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.900" />
      <ModalContent bg="black" m={0} borderRadius={0}>
        {/* Header */}
        <HStack 
          justify="space-between" 
          px={4} 
          py={4}
          borderBottom="1px solid"
          borderColor="gray.800"
        >
          <Text
            color="gray.400"
            fontSize="md"
            cursor="pointer"
            onClick={onClose}
            _hover={{ color: 'white' }}
          >
            Cancel
          </Text>
          <Text color="white" fontSize="md" fontWeight="semibold">
            Personal Limits
          </Text>
          <Text
            color="orange.400"
            fontSize="md"
            fontWeight="semibold"
            cursor={loading ? 'not-allowed' : 'pointer'}
            onClick={loading ? undefined : handleSave}
            opacity={loading ? 0.5 : 1}
            _hover={{ color: 'orange.300' }}
          >
            {loading ? <Spinner size="sm" /> : 'Save'}
          </Text>
        </HStack>

        <ModalBody py={6} px={4} overflowY="auto">
          <VStack spacing={6} align="stretch" maxW="500px" mx="auto">
            {/* Info Banner */}
            <Box 
              bg="green.900" 
              p={4} 
              borderRadius="xl"
              borderLeft="4px solid"
              borderColor="green.400"
            >
              <Text color="green.200" fontSize="sm">
                 Responsible gaming limits help you stay in control. Once enabled, 

                limits take effect immediately and cannot be increased for 24 hours.
              </Text>
            </Box>

            {/* Enable Toggle */}
            <HStack 
              bg="#1C1C1E" 
              p={4} 
              borderRadius="xl"
              justify="space-between"
            >
              <VStack align="start" spacing={0}>
                <Text color="white" fontWeight="semibold">
                  Enable Personal Limits
                </Text>
                <Text color="gray.500" fontSize="sm">
                  Activate spending and time controls
                </Text>
              </VStack>
              <Switch
                isChecked={limits.enabled}
                onChange={(e) => handleToggle(e.target.checked)}
                colorScheme="green"
                size="lg"
              />
            </HStack>

            {limits.enabled && (
              <>
                {/* Deposit Limits Section */}
                <Box>
                  <Text color="orange.400" fontSize="sm" fontWeight="semibold" mb={3}>
                    PURCHASE LIMITS
                  </Text>
                  
                  {/* Daily Limit */}
                  <Box bg="#1C1C1E" p={4} borderRadius="xl" mb={3}>
                    <HStack justify="space-between" mb={2}>
                      <Text color="gray.400" fontSize="sm">Daily Limit</Text>
                      <Text color="white" fontWeight="bold">${limits.dailyDepositLimit}</Text>
                    </HStack>
                    <Slider
                      value={limits.dailyDepositLimit}
                      onChange={(val) => handleLimitChange('dailyDepositLimit', val)}
                      min={10}
                      max={500}
                      step={10}
                    >
                      <SliderTrack bg="gray.700">
                        <SliderFilledTrack bg="orange.400" />
                      </SliderTrack>
                      <SliderThumb boxSize={5} bg="orange.400" />
                    </Slider>
                  </Box>

                  {/* Weekly Limit */}
                  <Box bg="#1C1C1E" p={4} borderRadius="xl" mb={3}>
                    <HStack justify="space-between" mb={2}>
                      <Text color="gray.400" fontSize="sm">Weekly Limit</Text>
                      <Text color="white" fontWeight="bold">${limits.weeklyDepositLimit}</Text>
                    </HStack>
                    <Slider
                      value={limits.weeklyDepositLimit}
                      onChange={(val) => handleLimitChange('weeklyDepositLimit', val)}
                      min={50}
                      max={2000}
                      step={50}
                    >
                      <SliderTrack bg="gray.700">
                        <SliderFilledTrack bg="orange.400" />
                      </SliderTrack>
                      <SliderThumb boxSize={5} bg="orange.400" />
                    </Slider>
                  </Box>

                  {/* Monthly Limit */}
                  <Box bg="#1C1C1E" p={4} borderRadius="xl">
                    <HStack justify="space-between" mb={2}>
                      <Text color="gray.400" fontSize="sm">Monthly Limit</Text>
                      <Text color="white" fontWeight="bold">${limits.monthlyDepositLimit}</Text>
                    </HStack>
                    <Slider
                      value={limits.monthlyDepositLimit}
                      onChange={(val) => handleLimitChange('monthlyDepositLimit', val)}
                      min={100}
                      max={10000}
                      step={100}
                    >
                      <SliderTrack bg="gray.700">
                        <SliderFilledTrack bg="orange.400" />
                      </SliderTrack>
                      <SliderThumb boxSize={5} bg="orange.400" />
                    </Slider>
                  </Box>
                </Box>

                {/* Session Limits Section */}
                <Box>
                  <Text color="orange.400" fontSize="sm" fontWeight="semibold" mb={3}>
                    SESSION LIMITS
                  </Text>

                  {/* Session Time Limit */}
                  <Box bg="#1C1C1E" p={4} borderRadius="xl" mb={3}>
                    <HStack justify="space-between" mb={2}>
                      <Text color="gray.400" fontSize="sm">Session Time Limit</Text>
                      <Text color="white" fontWeight="bold">{formatTime(limits.sessionTimeLimit)}</Text>
                    </HStack>
                    <Slider
                      value={limits.sessionTimeLimit}
                      onChange={(val) => handleLimitChange('sessionTimeLimit', val)}
                      min={15}
                      max={480}
                      step={15}
                    >
                      <SliderTrack bg="gray.700">
                        <SliderFilledTrack bg="orange.400" />
                      </SliderTrack>
                      <SliderThumb boxSize={5} bg="orange.400" />
                    </Slider>
                    <Text color="gray.600" fontSize="xs" mt={2}>
                      You'll receive a reminder when approaching this limit
                    </Text>
                  </Box>

                  {/* Loss Limit */}
                  <Box bg="#1C1C1E" p={4} borderRadius="xl">
                    <HStack justify="space-between" mb={2}>
                      <Text color="gray.400" fontSize="sm">Daily Loss Limit</Text>
                      <Text color="white" fontWeight="bold">${limits.lossLimit}</Text>
                    </HStack>
                    <Slider
                      value={limits.lossLimit}
                      onChange={(val) => handleLimitChange('lossLimit', val)}
                      min={5}
                      max={500}
                      step={5}
                    >
                      <SliderTrack bg="gray.700">
                        <SliderFilledTrack bg="orange.400" />
                      </SliderTrack>
                      <SliderThumb boxSize={5} bg="orange.400" />
                    </Slider>
                    <Text color="gray.600" fontSize="xs" mt={2}>
                      Game will pause when you reach this loss amount
                    </Text>
                  </Box>
                </Box>

                {/* Help Resources */}
                <Box 
                  bg="#1C1C1E" 
                  p={4} 
                  borderRadius="xl"
                  borderTop="1px solid"
                  borderColor="gray.800"
                >
                  <Text color="gray.400" fontSize="sm" fontWeight="semibold" mb={2}>
                    Need Help?
                  </Text>
                  <Text color="gray.500" fontSize="sm">
                    If you're concerned about your gaming habits, please reach out 
                    to the National Council on Problem Gambling at 1-800-522-4700 
                    or visit www.ncpgambling.org
                  </Text>
                </Box>
              </>
            )}

            {/* Spacer for safe area */}
            <Box h="80px" />
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PersonalLimitsModal;